import React, { Fragment, ReactNode, useEffect } from "react"
import Modal from "components/dialogs/Modal"
import SubmitButton from "components/Buttons/SubmitButton"
import CancelButton from "components/Buttons/CancelButton"
import DangerButton from "components/Buttons/DangerButton"
import Button from "components/Buttons/Button"
import { yupResolver } from "@hookform/resolvers/yup"
import { useForm } from "react-hook-form"
import { ButtonProps } from "@chakra-ui/react"
import ConfirmDelete, { Props as ConfirmDeleteProps } from "../Confirm/ConfirmDelete"

type Props = {
  title: ReactNode | string
  initialValues?: object
  onClose: (responseData?) => void
  onSubmit: (values) => any | Promise<any>
  onDanger?: ((values?) => void) | null
  onBack?: ((values?) => void)
  footerLeft?: ReactNode
  submitText?: string
  submitColorScheme?: ButtonProps["colorScheme"]
  children: ReactNode | ((props) => ReactNode)
  validationSchema?: any
  validate?: (values) => any
  noPadding?: boolean
  hideActions?: boolean
  icon?: string
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "full"
  h?: string
  dangerProps?: Omit<ConfirmDeleteProps, "children">
  closeOnEsc?: boolean
  loading?: boolean
  hideCancel?: boolean
  submitIsDisabled?: boolean
  onChange?: (values) => void
  disableSubmitWhenNotDirty?: boolean
  /** When set, show a single Close button instead of Cancel + Save while the form is pristine. */
  showCloseWhenNotDirty?: boolean
  mode?: "onChange" | "onBlur" | "onSubmit" | "onTouched" | "all"
  extraActions?: ReactNode | ReactNode[]
}

const ModalForm = ({
  initialValues = {},
  title,
  onClose,
  onSubmit,
  onDanger,
  onBack,
  footerLeft,
  submitText = "Save",
  submitColorScheme = "button.primary",
  children,
  validationSchema,
  validate,
  noPadding = false,
  hideActions = false,
  icon,
  size = "2xl",
  h,
  dangerProps,
  closeOnEsc = true,
  loading,
  hideCancel,
  submitIsDisabled,
  disableSubmitWhenNotDirty,
  showCloseWhenNotDirty,
  onChange,
  mode = "onChange",
  extraActions,
}: Props) => {
  const {
    control, handleSubmit, watch, formState: {
      isValid, isSubmitting, errors, isDirty,
    }, setValue, reset,
  } = useForm({
    defaultValues: initialValues,
    resolver: validationSchema ? yupResolver(validationSchema) : validate,
    mode,
  })

  useEffect(() => {
    const subscription = watch((data) => onChange?.(data))

    return () => subscription.unsubscribe()
  }, [watch, onChange])

  let resolvedChildren = children
  if (typeof children === "function") {
    resolvedChildren = children({
      control, watch, setValue, isValid, errors, handleSubmit, reset,
    })
  }

  const submitDisabled = () => {
    if (submitIsDisabled) {
      return true
    }

    if (disableSubmitWhenNotDirty && (!isDirty || !isValid)) {
      return true
    }

    return !isValid
  }

  const useCloseOnlyFooter = showCloseWhenNotDirty && !isDirty && !onDanger && !onBack

  let defaultActions: ReactNode[]

  if (useCloseOnlyFooter) {
    defaultActions = [
      <Button key="close" variant="outline" colorScheme="button.light" onClick={onClose}>
        Close
      </Button>,
    ]
  } else {
    defaultActions = [
      !hideCancel && <CancelButton onClick={onClose} />,
      <SubmitButton
        colorScheme={submitColorScheme}
        disabled={submitDisabled()}
        submitting={isSubmitting || loading}
        text={submitText}
      />]

    if (onDanger || onBack || footerLeft) {
      const values = watch()

      if (onDanger) {
        const DangerWrapper = dangerProps !== undefined ? ConfirmDelete : Fragment
        defaultActions.unshift(
          <DangerWrapper {...dangerProps}>
            <DangerButton onClick={() => onDanger(values)} />
          </DangerWrapper>,
        )
      } else if (onBack) {
        defaultActions.unshift(<Button variant="outline" colorScheme="button.light" onClick={() => onBack(values)}>Back</Button>)
      } else if (footerLeft) {
        defaultActions.unshift(footerLeft)
      }
    }
  }

  let actions = defaultActions

  if (extraActions && !hideActions) {
    const extras = Array.isArray(extraActions) ? extraActions : [extraActions]
    const submitIndex = actions.length - 1
    actions = [
      ...actions.slice(0, submitIndex),
      ...extras,
      ...actions.slice(submitIndex),
    ]
  }

  const resolvedActions = hideActions ? [] : actions

  return (
    <Modal
      show
      onSubmit={handleSubmit(onSubmit)}
      icon={icon}
      hide={onClose}
      title={title}
      actions={resolvedActions}
      noPadding={noPadding}
      size={size}
      h={h}
      closeOnEsc={closeOnEsc}
    >
      {resolvedChildren as ReactNode}
    </Modal>
  )
}

export default ModalForm
